import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useGlossary } from '../contexts/GlossaryContext';
import { fetchWallFeed } from '../lib/api';
import type { WallFeedItem, WallFeedResponse } from '../lib/types';
import { ROLE_OPTIONS, roleLabel, type RoleName } from '../lib/roles';
import '../styles/WallFeedPage.css';

interface WallFeedPageProps {
  onNavigate: (path: string) => void;
}

function formatCreatedAt(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function parseDetail(detail?: string): Record<string, string> | null {
  if (!detail) return null;
  try {
    const parsed = JSON.parse(detail) as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(parsed).map(([key, value]) => [key, String(value)]),
    );
  } catch {
    return null;
  }
}

function pickDetailValue(detail: Record<string, string> | null, key: string): string {
  const value = detail?.[key] ?? '';
  return value && value !== 'null' && value !== 'undefined' ? value : '';
}

function renderMarkdown(text: string) {
  return (
    <div className="wall-feed-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}

function WallFeedCard({
  item,
  onOpenEdit,
  onOpenView,
}: {
  item: WallFeedItem;
  onOpenEdit: (path: string) => void;
  onOpenView: (path: string) => void;
}) {
  const detail = parseDetail(item.detail);
  const seedId = pickDetailValue(detail, 'seed_id');
  const segmentId = pickDetailValue(detail, 'segment_id');
  const editUrl = pickDetailValue(detail, 'edit_url') || (seedId ? `/studio?seed_id=${encodeURIComponent(seedId)}&mode=edit${segmentId ? `&segment_id=${encodeURIComponent(segmentId)}` : ''}` : '');
  const viewUrl = pickDetailValue(detail, 'view_url') || (seedId ? `/studio?seed_id=${encodeURIComponent(seedId)}&mode=view${segmentId ? `&segment_id=${encodeURIComponent(segmentId)}` : ''}` : '');
  return (
    <article className="wall-feed-card">
      <div className="wall-feed-card__head">
        <span className={`wall-feed-badge ${item.response_required ? 'wall-feed-badge--required' : 'wall-feed-badge--optional'}`}>
          {item.response_required ? 'Required' : 'Optional'}
        </span>
        <span className="wall-feed-card__time">{formatCreatedAt(item.created_at)}</span>
      </div>
      <h3>{item.title}</h3>
      {renderMarkdown(item.summary)}
      <dl className="wall-feed-meta">
        <div><dt>Role</dt><dd>{item.role_label}</dd></div>
        <div><dt>Actor</dt><dd>{item.actor_name}</dd></div>
        <div><dt>Type</dt><dd>{item.event_type}</dd></div>
      </dl>
      {detail && (
        <div className="wall-feed-detail">
          {detail.comment && renderMarkdown(detail.comment)}
          {Object.entries(detail).map(([key, value]) => {
            if (['comment', 'edit_url', 'view_url'].includes(key)) return null;
            return (
              <span key={key} className="wall-feed-detail__chip">
                {key}: {value}
              </span>
            );
          })}
        </div>
      )}
      {(editUrl || viewUrl) && (
        <div className="wall-feed-actions">
          {editUrl && (
            <button type="button" className="wall-feed-action" onClick={() => onOpenEdit(editUrl)}>
              Open edit
            </button>
          )}
          {viewUrl && (
            <button type="button" className="wall-feed-action wall-feed-action--secondary" onClick={() => onOpenView(viewUrl)}>
              Open view
            </button>
          )}
        </div>
      )}
    </article>
  );
}

export default function WallFeedPage({ onNavigate }: WallFeedPageProps) {
  const { locale } = useGlossary();
  const { authLoading, profile } = useAuth();
  const [data, setData] = useState<WallFeedResponse | null>(null);
  const [activeRole, setActiveRole] = useState<RoleName>('शिक्षार्थी');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const activeGroup = useMemo(
    () => data?.roles.find((group) => group.role === activeRole),
    [activeRole, data],
  );

  useEffect(() => {
    if (profile?.activeRole) {
      setActiveRole(profile.activeRole);
    }
  }, [profile?.activeRole]);

  useEffect(() => {
    let alive = true;
    if (authLoading) {
      setLoading(true);
      return () => {
        alive = false;
      };
    }
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          throw new Error('Sign in to view your wall feed.');
        }
        const response = await fetchWallFeed(token, 9);
        if (!alive) return;
        setData(response);
      } catch (err) {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Failed to load wall feed.');
      } finally {
        if (alive) setLoading(false);
      }
    };
    void load();
    return () => {
      alive = false;
    };
  }, [authLoading]);

  return (
    <div className="page-container wall-feed-page">
      <header className="wall-feed-hero">
        <div>
          <p className="wall-feed-eyebrow">Your role walls</p>
          <h1>Wall Feed</h1>
          <p className="wall-feed-summary">Text-only updates for the last 9 days across all four roles.</p>
        </div>
        <button className="wall-feed-back" onClick={() => onNavigate('/')}>Home</button>
      </header>

      <div className="wall-feed-tabs">
        {ROLE_OPTIONS.map((role) => {
          const group = data?.roles.find((entry) => entry.role === role);
          const active = role === activeRole;
          const activity = profile?.roleActivity?.[role] ?? 0;
          return (
            <button
              key={role}
              className={`wall-feed-tab ${active ? 'is-active' : ''}`}
              onClick={() => setActiveRole(role)}
            >
              <span>{roleLabel(role, locale)}</span>
              <strong>{activity} · {group?.count ?? 0}</strong>
            </button>
          );
        })}
      </div>

      {loading ? <p className="wall-feed-state">Loading…</p> : null}
      {error ? <p className="wall-feed-state wall-feed-state--error">{error}</p> : null}

      {!loading && !error && (
        <section className="wall-feed-panel">
          <div className="wall-feed-panel__head">
            <h2>{activeGroup?.role_label ?? roleLabel(activeRole, locale)}</h2>
            <span>{activeGroup?.count ?? 0} updates</span>
          </div>
          <div className="wall-feed-list">
            {(activeGroup?.items ?? []).length > 0 ? (
              activeGroup!.items.map((item) => (
                <WallFeedCard
                  key={item.id}
                  item={item}
                  onOpenEdit={onNavigate}
                  onOpenView={onNavigate}
                />
              ))
            ) : (
              <p className="wall-feed-empty">No recent updates in this role wall.</p>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
